import { useState, useEffect, useCallback } from "react";

export type PageOrientation = 'portrait' | 'landscape';

const PORTRAIT_QUERY = "(orientation: portrait)";

function readOrientation(): PageOrientation {
  if (typeof window.matchMedia !== 'function') {
    return 'landscape';
  }
  return window.matchMedia(PORTRAIT_QUERY).matches ? 'portrait' : 'landscape';
}

export default function usePageOrientation(): PageOrientation {
  // Read synchronously: consumers that configure a device at mount (the camera
  // encoder profile) cannot recover from one render of a wrong orientation —
  // Safari refuses to renegotiate the capture size of a live track.
  const [orientation, setOrientation] = useState<PageOrientation>(readOrientation);

  const onOrientationChange = useCallback((event: MediaQueryListEvent | MediaQueryList) => {
    if (event.matches) {
      setOrientation('portrait');
    } else {
      setOrientation('landscape');
    }
  }, []);

  useEffect(function setInitialOrientation() {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const matchOrientation = window.matchMedia(PORTRAIT_QUERY);

    onOrientationChange(matchOrientation);

    matchOrientation.addEventListener(
      'change', onOrientationChange
    );

    return () => {
      matchOrientation.removeEventListener('change', onOrientationChange);
    };
  }, [setOrientation, onOrientationChange]);

  return orientation;
}
